/* ==========================================================================
   Forced Change Password Page (PORTAL-75)
   Shown when a provider's password was set by the pharmacy. The provider must
   change it before accessing protected portal routes. Reached via the redirect
   in ProtectedRoute; the provider can still sign out.
   ========================================================================== */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, CheckCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { PharmacyLogo } from '@/components/ui/PharmacyLogo'
import { changePassword } from '@/api/auth'
import { ApiClientError } from '@/api/client'
import { useToast } from '@/context/ToastContext'
import { useAuth } from '@/hooks/useAuth'
import { usePortalSlug } from '@/context/PortalContext'

/* --------------------------------------------------------------------------
   Password Requirements (mirrors ChangePasswordModal)
   -------------------------------------------------------------------------- */

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'At least one number', test: (p) => /\d/.test(p) },
]

function PasswordRequirements({ password }: { password: string }) {
  return (
    <div className="mt-2 space-y-1">
      {PASSWORD_REQUIREMENTS.map((req, index) => {
        const met = req.test(password)
        return (
          <div
            key={index}
            className={`flex items-center gap-2 text-sm transition-colors ${
              met ? 'text-green-600' : 'text-charcoal/50'
            }`}
          >
            <CheckCircle
              className={`h-4 w-4 ${met ? 'opacity-100' : 'opacity-30'}`}
              aria-hidden="true"
            />
            <span>{req.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ChangePassword() {
  const navigate = useNavigate()
  const slug = usePortalSlug()
  const { success: showSuccess } = useToast()
  const { refreshProfile, logout } = useAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | undefined>()

  function validate(): string | undefined {
    if (!currentPassword) return 'Current password is required'
    if (!newPassword) return 'New password is required'
    if (newPassword.length < 8) return 'New password must be at least 8 characters'
    if (new TextEncoder().encode(newPassword).length > 72) return 'New password must be at most 72 characters (fewer if using non-English characters or emoji)'
    if (!/[A-Z]/.test(newPassword)) return 'New password must contain at least one uppercase letter'
    if (!/[a-z]/.test(newPassword)) return 'New password must contain at least one lowercase letter'
    if (!/\d/.test(newPassword)) return 'New password must contain at least one number'
    if (newPassword === currentPassword) return 'New password must be different from current password'
    if (newPassword !== confirmPassword) return 'Passwords do not match'
    return undefined
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const error = validate()
    if (error) {
      setApiError(error)
      return
    }

    setLoading(true)
    setApiError(undefined)

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      })
      showSuccess('Password changed successfully!')
      // Refresh the profile so must_change_password is cleared, then continue.
      // RequireProfile will forward to /profile if the profile is incomplete.
      await refreshProfile()
      navigate(`/rx/${slug}/prescribe`, { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setApiError(err.getUserMessage())
      } else {
        setApiError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleSignOut() {
    await logout()
    navigate(`/rx/${slug}/login`, { replace: true })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <PharmacyLogo size="xl" linkTo="/" />
        </div>

        <Card className="p-6 sm:p-8">
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-2">
            Update your password
          </h1>
          <p className="text-sm text-charcoal/70 text-center mb-6">
            For your security, please set a new password before continuing.
          </p>

          {apiError && (
            <Alert variant="error" className="mb-6">
              {apiError}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Current Password */}
            <FormField label="Current Password" required htmlFor="current-password">
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value)
                    setApiError(undefined)
                  }}
                  disabled={loading}
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors"
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  disabled={loading}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </FormField>

            {/* New Password */}
            <FormField label="New Password" required htmlFor="new-password">
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  maxLength={72}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    setApiError(undefined)
                  }}
                  disabled={loading}
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors"
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  disabled={loading}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <PasswordRequirements password={newPassword} />
            </FormField>

            {/* Confirm New Password */}
            <FormField label="Confirm New Password" required htmlFor="confirm-password">
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  maxLength={72}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setApiError(undefined)
                  }}
                  disabled={loading}
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
              )}
            </FormField>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading}
              className="w-full mt-2"
            >
              Update Password
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={loading}
              className="text-sm text-charcoal/50 hover:text-charcoal/70 transition-colors"
            >
              Sign out
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default ChangePassword
